import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Briefcase, Loader2, Search } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ClientStatusPill({ status }) {
  const colours = { Active: "bg-lime text-forest", Inactive: "bg-soft text-mid-neutral" };
  return (
    <span className={"inline-block rounded-full px-3 py-1 text-xs font-bold shrink-0 " + (colours[status] || "bg-soft text-forest")}>
      {status}
    </span>
  );
}

export default function ClientsList() {
  const [clients, setClients] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async (query = "") => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/clients`, { params: query ? { q: query } : {} });
      setClients(r.data.clients);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  return (
    <div data-testid="admin-clients" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Clients</h1>
          <p className="mt-2 text-mid-neutral">
            Post-sale relationships, services and billing details.
          </p>
        </div>
        <form onSubmit={search} className="flex items-center gap-2">
          <input
            data-testid="clients-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients…"
            className="p-2.5 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest w-56"
          />
          <button
            type="submit"
            data-testid="clients-search-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold"
          >
            <Search className="h-4 w-4" /> Search
          </button>
        </form>
      </header>

      {error && (
        <div data-testid="clients-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!clients ? (
        <div className="flex items-center justify-center h-40 text-mid-neutral">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading clients…
        </div>
      ) : (
        <div data-testid="clients-list" className="rounded-2xl bg-white border border-border-neutral overflow-hidden">
          {clients.length === 0 && (
            <div className="p-8 text-center text-sm text-mid-neutral">
              <Briefcase className="h-6 w-6 mx-auto mb-2 text-mint" />
              No clients yet. Mark a lead as <strong>Won</strong> and use <strong>Convert to Client</strong> on the lead page.
            </div>
          )}
          {clients.map((c) => (
            <Link
              key={c.id}
              to={`/admin/clients/${c.id}`}
              data-testid={`client-row-${c.id}`}
              className="flex items-center gap-4 p-4 border-b border-border-neutral last:border-0 hover:bg-soft/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold text-forest truncate">
                  {c.businessName || `${c.contactFirstName} ${c.contactLastName}`}
                  <span className="text-mid-neutral font-normal"> · {c.email}</span>
                </p>
                <p className="text-xs text-mid-neutral truncate">
                  {c.contactFirstName} {c.contactLastName}
                  {c.phone ? ` · ${c.phone}` : ""} · {c.activeServices} active service{c.activeServices === 1 ? "" : "s"}
                </p>
              </div>
              <ClientStatusPill status={c.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
